<template>
    <nav class="sticky top-0 z-50 border-b border-app bg-app/80 backdrop-blur-md h-[72px] text-body">
        <div class="mx-auto flex h-full max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <!-- Logo -->
            <NuxtLink to="/" class="flex items-center gap-2 group" @click="closeMobile">
                <div class="flex h-10 w-auto items-center justify-center ">
                    <BrandLogo class="w-28 h-10 sm:w-32 sm:h-12" :is-public="true"></BrandLogo>
                </div>
            </NuxtLink>

            <!-- Navigation Links (desktop) -->
            <div class="hidden gap-8 md:flex md:items-center">
                <template v-for="link in headerMenu" :key="link.id ">
                    <NuxtLink v-if="['default', 'page'].includes(link.type)" :to="link.link"
                        class="text-sm font-medium  transition-colors hover:text-brand-500"
                        active-class="text-brand-500 font-bold">
                        {{ link.name }}
                    </NuxtLink>
                    <a v-else :href="link.link"
                        class="text-sm font-medium transition-colors hover:text-brand-500">
                        {{ link.name }}
                    </a>
                </template>
                <button type="button" role="switch" :aria-checked="generalStore.theme == 'dark'"
                    @click="generalStore.toggleTheme()"
                    class="relative inline-flex h-4 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-0"
                    :class="generalStore.theme == 'dark' ? 'bg-brand-500/40' : 'bg-gray-200 dark:bg-card hover:bg-gray-200 dark:hover:bg-gray-700'">
                    <span
                        class="inline-flex h-7 w-7 transform items-center justify-center rounded-full text-card bg-gray-200 dark:bg-brand-500 dark:text-on-brand text-[16px] transition-transform duration-300"
                        :class="generalStore.theme == 'dark' ? 'translate-x-[28px]' : 'translate-x-[0px]'">
                        <i :class="generalStore.theme == 'dark' ? 'ye ye-moon' : 'ye ye-sun'"></i>
                    </span>
                </button>
            </div>

            <!-- Actions (desktop) -->
            <div class="hidden items-center gap-4 md:flex">
                <template v-if="authStore.member">
                    <NuxtLink :to="{ name: 'Logout' }" class="block text-sm font-semibold text-brand-500 hover:text-brand-600 me-3">{{ $t("website.logout") }}</NuxtLink>
                    <NuxtLink :to="{ name: 'AccountInfo' }" class="btn btn-primary rounded-full text-sm font-semibold ">
                        <span class="hidden sm:block me-2">{{ $t("website.member") }}</span>
                        <span>{{ $t("website.account") }}</span>
                    </NuxtLink>
                </template>
                <template v-else-if="authStore.isAuthenticated">
                    <NuxtLink :to="{ name: 'Logout' }" class="text-sm font-semibold text-brand-500 hover:text-brand-600">{{ $t("website.logout") }}</NuxtLink>
                </template>
                <template v-else>
                    <NuxtLink to="/login" class="block text-sm font-semibold text-brand-500 hover:text-brand-600 me-3">{{ $t("website.login") }}</NuxtLink>
                    <NuxtLink to="/member-registration"
                        class="rounded-full bg-brand-500 px-6 py-2.5 text-sm font-semibold text-on-brand transition-all hover:bg-brand-600 hover:shadow-lg hover:shadow-brand-500/25 active:scale-95">
                        {{ $t("website.register") }}
                    </NuxtLink>
                </template>
            </div>

            <!-- Mobile controls -->
            <div class="flex items-center gap-1 md:hidden">
                <button type="button" role="switch" :aria-checked="generalStore.theme == 'dark'"
                    :aria-label="generalStore.theme == 'dark' ? 'Switch to light theme' : 'Switch to dark theme'"
                    @click="generalStore.toggleTheme()"
                    class="flex h-10 w-10 items-center justify-center rounded-full text-lg text-body transition-colors hover:bg-card">
                    <i :class="generalStore.theme == 'dark' ? 'ye ye-moon' : 'ye ye-sun'"></i>
                </button>
                <button type="button" :aria-expanded="mobileOpen" aria-controls="mobile-menu"
                    :aria-label="mobileOpen ? 'Close menu' : 'Open menu'"
                    @click="toggleMobile"
                    class="flex h-10 w-10 items-center justify-center rounded-full text-xl text-body transition-colors hover:bg-card">
                    <i :class="mobileOpen ? 'ye ye-xmark' : 'ye ye-bars'"></i>
                </button>
            </div>
        </div>

        <!-- Mobile menu -->
        <Transition name="fade">
            <div v-if="mobileOpen" class="fixed inset-0 top-[72px] z-40 bg-black/40 md:hidden" @click="closeMobile"></div>
        </Transition>
        <Transition name="slide-down">
            <div v-if="mobileOpen" id="mobile-menu"
                class="absolute inset-x-0 top-[72px] z-50 max-h-[calc(100vh-72px)] overflow-y-auto border-b border-app bg-app shadow-lg md:hidden">
                <div class="flex flex-col px-4 py-3 sm:px-6">
                    <template v-for="link in headerMenu" :key="link.id">
                        <NuxtLink v-if="['default', 'page'].includes(link.type)" :to="link.link"
                            class="rounded-lg px-3 py-3 text-base font-medium transition-colors hover:bg-card hover:text-brand-500"
                            active-class="text-brand-500 font-bold" @click="closeMobile">
                            {{ link.name }}
                        </NuxtLink>
                        <a v-else :href="link.link"
                            class="rounded-lg px-3 py-3 text-base font-medium transition-colors hover:bg-card hover:text-brand-500"
                            @click="closeMobile">
                            {{ link.name }}
                        </a>
                    </template>

                    <div class="my-2 border-t border-app"></div>

                    <template v-if="authStore.member">
                        <NuxtLink :to="{ name: 'AccountInfo' }"
                            class="rounded-full bg-brand-500 px-5 py-3 text-center text-sm font-semibold text-on-brand transition-all active:scale-95"
                            @click="closeMobile">
                            {{ $t("website.member") }} · {{ $t("website.account") }}
                        </NuxtLink>
                        <NuxtLink :to="{ name: 'Logout' }"
                            class="mt-2 px-3 py-3 text-center text-sm font-semibold text-brand-500 hover:text-brand-600"
                            @click="closeMobile">
                            {{ $t("website.logout") }}
                        </NuxtLink>
                    </template>
                    <template v-else-if="authStore.isAuthenticated">
                        <NuxtLink :to="{ name: 'Logout' }"
                            class="px-3 py-3 text-center text-sm font-semibold text-brand-500 hover:text-brand-600"
                            @click="closeMobile">
                            {{ $t("website.logout") }}
                        </NuxtLink>
                    </template>
                    <template v-else>
                        <NuxtLink to="/member-registration"
                            class="rounded-full bg-brand-500 px-5 py-3 text-center text-sm font-semibold text-on-brand transition-all active:scale-95"
                            @click="closeMobile">
                            {{ $t("website.register") }}
                        </NuxtLink>
                        <NuxtLink to="/login"
                            class="mt-2 px-3 py-3 text-center text-sm font-semibold text-brand-500 hover:text-brand-600"
                            @click="closeMobile">
                            {{ $t("website.login") }}
                        </NuxtLink>
                    </template>
                </div>
            </div>
        </Transition>
    </nav>
</template>

<script setup lang="ts">
import { useAuthStore, useGeneralStore } from '@core/global';
import BrandLogo from '@core/components/BrandLogo.vue'
import { MenuItem, WebConfig } from '@core/stores/types';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const authStore = useAuthStore();
const generalStore = useGeneralStore();
const props = defineProps<{ webConfig: WebConfig }>()
const route = useRoute();

const mobileOpen = ref(false);

const toggleMobile = () => { mobileOpen.value = !mobileOpen.value; };
const closeMobile = () => { mobileOpen.value = false; };

// Lock body scroll while the mobile menu is open
watch(mobileOpen, (open) => {
    if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : '';
    }
});

// Close on navigation
watch(() => route.fullPath, closeMobile);

const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeMobile();
};
if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKeydown);
}
onBeforeUnmount(() => {
    if (typeof window !== 'undefined') window.removeEventListener('keydown', onKeydown);
    if (typeof document !== 'undefined') document.body.style.overflow = '';
});

// Membership
// MembershipRegistration
// Updates
// FAQs
// About
// Contact
// Blog
// MemberAccount
const defaultHeaderMenu:MenuItem[] = [
    { id: "Home", name: "Home", type: "default", link: { name: "Membership"} },
    { id: "About", name: "About", type: "default", link: { name: "About"} },
    { id: "Updates", name: "Updates", type: "default", link: { name: "Updates"} },
    { id: "FAQs", name: "FAQs", type: "default", link: { name: "FAQs"} },
    { id: "Blog", name: "Blog", type: "default", link: { name: "Blog"} },
    { id: "Events", name: "Events", type: "default", link: { name: "Events"} },
    { id: "Membership", name: "Membership", type: "default", link: { name: "MembershipRegistration"} },
    { id: "Contact", name: "Contact", type: "default", link: { name: "Contact"} },
];

const headerMenu = computed(()=>{
    // return props.webConfig.headerMenu? props.webConfig.headerMenu: defaultHeaderMenu;
    return defaultHeaderMenu;
})
</script>

<style scoped>
.border-app {
    border-color: rgb(var(--core-app-border));
}

.bg-app {
    background-color: rgb(var(--core-app-bg));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
